import * as uuid from 'uuid';
import { DomainCoreGeneration } from '../libraries/DomainCoreGeneration';
import { reduceRight, reduceWithParent } from './OrderHelpers';
import check from '../libraries/check-types-ext';
import ProductSelection from './ProductSelection';
import AppError from '../libraries/AppError';
import { getLegacyTotalPriceWithDiscount, getLegacyTotalPriceWithoutDiscount } from './PriceCalculationLegacy';
const calculateLineTotalRespectingDiscountQuantity = (parentAcc, currentAccValue, curr) => {
    if (curr.priceBefore && curr.discountQuantity && curr.discountQuantity < curr.quantity) {
        return (parentAcc +
            (currentAccValue + (curr.price || 0)) * curr.discountQuantity +
            (currentAccValue + (curr.priceBefore || 0)) * (curr.quantity - curr.discountQuantity));
    }
    return parentAcc + (currentAccValue + (curr.price || 0)) * (curr.quantity || 1);
};
export function computeTotalPrice(topLevelOrderLines) {
    reduceRight(topLevelOrderLines, (acc, curr) => {
        curr.virtualId = uuid.v4();
        return null;
    }, null);
    const r1 = reduceWithParent(topLevelOrderLines, (acc, curr, parent) => {
        var _a;
        const currentAccValue = acc.accumulations[curr.virtualId] || 0;
        if (parent === null) {
            if (curr.type === 'Discount' || curr.type === 'FreeMeal' || curr.type === 'Delivery') {
                return Object.assign(Object.assign({}, acc), { total: acc.total + curr.totalPrice });
            }
            if (curr.type === 'CombinatorLine') {
                return Object.assign(Object.assign({}, acc), { total: acc.total + curr.totalPriceAccum });
            }
            const calculateLineTotal = (currentValue, currentOrderLine) => {
                var _a, _b, _c, _d, _e;
                if (currentOrderLine.type !== 'Product') {
                    return currentValue;
                }
                const isQuantityHigherThenDiscountQuantity = currentOrderLine.quantity > currentOrderLine.discountQuantity;
                const shouldLimitDiscountByQuantity = ((_b = (_a = currentOrderLine === null || currentOrderLine === void 0 ? void 0 : currentOrderLine.discount) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.quantityLimit) ||
                    ((_e = (_d = (_c = currentOrderLine === null || currentOrderLine === void 0 ? void 0 : currentOrderLine.discount) === null || _c === void 0 ? void 0 : _c.options) === null || _d === void 0 ? void 0 : _d.qualifyingProductIds) === null || _e === void 0 ? void 0 : _e.length);
                if (shouldLimitDiscountByQuantity &&
                    currentOrderLine.discountQuantity >= 0 &&
                    isQuantityHigherThenDiscountQuantity) {
                    return currentOrderLine.totalPriceAccum;
                }
                if (currentOrderLine.discountQuantity > 0 && isQuantityHigherThenDiscountQuantity) {
                    return currentOrderLine.totalPrice;
                }
                if (!currentOrderLine.price) {
                    return currentValue < -0.01 ? currentOrderLine.totalPriceBeforeAccum : currentValue;
                }
                return (currentValue + currentOrderLine.price) * (currentOrderLine.quantity || 1);
            };
            const lineTotal = calculateLineTotal(currentAccValue, curr);
            return {
                accumulations: acc.accumulations,
                total: acc.total + lineTotal,
            };
        }
        if (parent !== null && parent.type !== 'CombinatorLine') {
            const parentAcc = acc.accumulations[parent.virtualId] || 0;
            if (curr.type === 'Product') {
                if (parent.options && parent.options.freeQuantity) {
                    return {
                        accumulations: Object.assign(Object.assign({}, acc.accumulations), { [parent.virtualId]: 'totalPriceAccum' in parent ? parent.totalPriceAccum : 0 }),
                        total: acc.total,
                    };
                }
                const price = calculateLineTotalRespectingDiscountQuantity(parentAcc, currentAccValue, curr);
                return {
                    accumulations: Object.assign(Object.assign({}, acc.accumulations), { [parent.virtualId]: price, [`${parent.virtualId}_totalPriceBeforeAccum`]: ((_a = curr.totalPriceBeforeAccum) !== null && _a !== void 0 ? _a : curr.totalPrice) * (curr.quantity || 1) }),
                    total: acc.total,
                };
            }
            else {
                const currentValue = curr.type === 'CombinatorLine' ? curr.totalPriceAccum : currentAccValue;
                return {
                    accumulations: Object.assign(Object.assign({}, acc.accumulations), { [parent.virtualId]: parentAcc + currentValue, [`${parent.virtualId}_totalPriceBeforeAccum`]: curr.type === 'CombinatorLine' ? curr.totalPriceBeforeAccum : 0 }),
                    total: acc.total,
                };
            }
        }
        return acc;
    }, { total: 0, accumulations: {} }, null);
    if (r1.total < -0.01) {
        return Object.entries(r1.accumulations).reduce((sum, [key, value]) => (key.endsWith('_totalPriceBeforeAccum') ? sum + value : sum), 0);
    }
    if (Math.abs(r1.total) < 0.01) {
        r1.total = 0;
    }
    return r1.total;
}
export function getTotalPriceWithDiscount(orderLines, productCatalog, { includeReturnableItems = false, perLine = false, vouchers = [] } = {}) {
    if (DomainCoreGeneration.detectObjectGeneration(orderLines) >= DomainCoreGeneration.Generation4) {
        return computeTotalPrice(orderLines);
    }
    else if (DomainCoreGeneration.detectObjectGeneration(orderLines) === DomainCoreGeneration.Generation3) {
        const priceList = orderLines
            .filter((line) => 'totalPriceAccum' in line && line.totalPriceAccum)
            .map((line) => {
            return 'totalPriceAccum' in line ? line.totalPriceAccum : 0;
        });
        if (perLine) {
            return priceList;
        }
        return priceList.reduce((a, b) => a + b, 0);
    }
    return getLegacyTotalPriceWithDiscount(orderLines, productCatalog, { includeReturnableItems, perLine, vouchers }, getTotalPriceWithoutDiscount);
}
export function getTotalPriceWithoutDiscount(orderLines, includeReturnableItems) {
    if (DomainCoreGeneration.detectObjectGeneration(orderLines) >= DomainCoreGeneration.Generation3) {
        return orderLines
            .map((line) => line.totalPriceBeforeAccum || line.totalPriceAccum || 0)
            .reduce((a, b) => a + b);
    }
    return getLegacyTotalPriceWithoutDiscount(orderLines, includeReturnableItems, getTotalPriceWithoutDiscount);
}
export function getTotalPrice(lines, productCatalog, { isOrderLines = false, includeReturnableItems = false, options = {}, applyPriceRules = true, perLine = false, orderTags = [], generation = 1, } = {}) {
    if (!isOrderLines) {
        check.number.orFail(generation, 'generation');
        if (!productCatalog) {
            throw AppError.ProductCatalogInvalid();
        }
    }
    const orderLines = isOrderLines
        ? lines
        : ProductSelection.mapToOrderLinesGen(generation)(productCatalog, lines, includeReturnableItems, options, orderTags);
    if (applyPriceRules) {
        return getTotalPriceWithDiscount(orderLines, productCatalog, Object.assign(Object.assign({}, options), { includeReturnableItems,
            perLine }));
    }
    if (perLine) {
        return orderLines.map((line) => getTotalPriceWithoutDiscount([line], includeReturnableItems));
    }
    return getTotalPriceWithoutDiscount(orderLines, includeReturnableItems);
}
export function getTotalGlobalDiscount(lines, productCatalog, { isOrderLines = false, includeReturnableItems = false, options = {}, orderTags = [], generation = 0 } = {}) {
    if (!isOrderLines) {
        check.number.orFail(generation, 'generation');
        if (!productCatalog) {
            throw AppError.ProductCatalogInvalid();
        }
    }
    const orderLines = isOrderLines
        ? lines
        : ProductSelection.mapToOrderLinesGen(generation)(productCatalog, lines, includeReturnableItems, options, orderTags);
    const totalPriceWithDiscount = getTotalPriceWithDiscount(orderLines, productCatalog, Object.assign(Object.assign({}, options), { includeReturnableItems, perLine: false }));
    const totalPriceWithoutDiscount = getTotalPriceWithoutDiscount(orderLines, includeReturnableItems);
    return Math.round((totalPriceWithoutDiscount - totalPriceWithDiscount) * 100) / 100;
}
export function getTotalPriceCents(lines, productCatalog, { isOrderLines = false, includeReturnableItems = false, orderTags = [], options = {}, generation = 0, applyPriceRules = true, } = {}) {
    return Math.round(getTotalPrice(lines, productCatalog, {
        isOrderLines,
        includeReturnableItems,
        orderTags,
        options,
        generation,
        applyPriceRules,
    }) * 100);
}
